import { createContext, useContext, useState, type ReactNode } from 'react';
import { Modal } from '../components/ui/Modal';
import type { ModalProps } from '../components/ui/Modal';
import type { ModalType } from '../components/ui/Modal';

type ModalContextProps = {
  showModal: (options: {
    title?: string;
    description?: string;
    type?: ModalType;
    actions?: ReactNode;
    onClose?: () => void;
  }) => void;
  hideModal: () => void;
};

const ModalContext = createContext<ModalContextProps | undefined>(undefined);

export function useModal() {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error('useModal must be used within ModalProvider');
  return ctx;
}

export function ModalProvider({ children }: { children: ReactNode }) {
  const [modalProps, setModalProps] = useState<Omit<ModalProps, 'open'> & { open: boolean }>({
    open: false,
    title: '',
    description: '',
    type: 'normal',
    actions: undefined,
    onClose: () => setModalProps(m => ({ ...m, open: false })),
  });

  const showModal = ({
    title,
    description,
    type = 'normal',
    actions,
    onClose,
  }: {
    title?: string;
    description?: string;
    type?: ModalType;
    actions?: ReactNode;
    onClose?: () => void;
  }) => {
    setModalProps({
      open: true,
      title,
      description,
      type,
      actions,
      onClose: () => {
        setModalProps(m => ({ ...m, open: false }));
        if (onClose) onClose();
      },
    });
  };

  const hideModal = () => setModalProps(m => ({ ...m, open: false }));

  return (
    <ModalContext.Provider value={{ showModal, hideModal }}>
      {children}
      <Modal {...modalProps} />
    </ModalContext.Provider>
  );
}